import {
  Button,
  Divider,
  FlexBox,
  Modal,
  ModalBody,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { PropsWithChildren } from 'lib-shared-types-utils';
import { useCallback, useState } from 'react';

import { appActions } from 'app/app.actions';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import {
  WhatsAppLoginButton,
  WhatsAppSignUpData,
  WhatsAppSingUpButtonProps,
} from '../../../../whatsAppLoginButton';

import { WhatsAppConnectModal } from './components/whatsAppConnectModal/whatsAppConnectModal';
import styles from './whatsAppAuthenticator.module.scss';

type WhatsAppSignUpProps = PropsWithChildren & {
  /** The id of the facebook app. */
  appId: string;
  /** The props of the facebook login button. */
  facebookButtonProps?: Omit<
    WhatsAppSingUpButtonProps,
    'scope' | 'onSuccess' | 'onError'
  >;
  handleAddPhoneNumber: (arg: { wabaId: string }) => Promise<void>;
};

/**
 * A component that handles authenticating to Facebook and setting up the
 * WhatsApp channel.
 */
export const WhatsAppAuthenticator = ({
  handleAddPhoneNumber,
  facebookButtonProps,
  children,
}: WhatsAppSignUpProps): JSX.Element => {
  const dispatch = useAppDispatch();

  const [prerequisitesModalOpen, setPrerequisitesModalOpen] = useState(false);

  /** Handles closing the modal. */
  const handleClose = useCallback(() => {
    setPrerequisitesModalOpen(false);
  }, []);

  /** Handles the whatsapp sing up. */
  const handleWhatsAppAuthenticated = async (
    whatsAppSignUpData: WhatsAppSignUpData,
  ) => {
    await dispatch(
      appActions.showModal({
        modal: {
          type: 'settings:whatsAppConnect',
          arg: {
            whatsAppSignUpData,
          },
        },
      }),
    );
  };

  /** The authentication prerequisites. */
  const prerequisites: string[] = [
    'To create a Whatsapp Business Account, you will need an existing Meta Business Manager account with a valid Business Portfolio, and a Facebook user with admin role on that account.',
    'If you are linking a number to your Whatsapp Business Account, it should have either voice capability or sms capability to receive a verification code. If the number is created through Dixa it can only be verified with voice capability.',
    'You must have at least one phone number to link to your account.',
  ];

  /**
   * These are tips that can help admins understand how the integration works
   * and help them not mess it up.
   */
  const tips: string[] = [
    'If the given phone number is already associated with a different Meta Business Portfolio, it will need to be removed from that portfolio before proceeding.',
  ];

  return (
    <>
      <Button
        onClick={() => setPrerequisitesModalOpen(true)}
        {...facebookButtonProps}
      >
        {children}
      </Button>

      <Modal
        isOpen={prerequisitesModalOpen}
        className={styles.modal}
        onClose={handleClose}
      >
        <ModalHead title="WhatsApp for Business" onClose={handleClose} />

        <ModalBody>
          <FlexBox gap={'300'} flexDirection="column">
            <Text.Paragraph>
              To use WhatsApp you’ll need to create a WhatsApp Business Account.
              If you already have created one through Dixa you can add numbers
              to it here.
            </Text.Paragraph>
            <Divider />
            <Text.Paragraph>{'What you need'}</Text.Paragraph>
            <ul>
              {prerequisites.map((text, index) => (
                <li key={index}>
                  <Text.Paragraph>{text}</Text.Paragraph>
                </li>
              ))}
            </ul>
            <Text.Paragraph>{'Also good to know'}</Text.Paragraph>
            <ul>
              {tips.map((text, index) => (
                <li key={index}>
                  <Text.Paragraph>{text}</Text.Paragraph>
                </li>
              ))}
            </ul>
          </FlexBox>
        </ModalBody>

        <FlexBox justifyContent="flex-end" gap="300" padding="500">
          <Button variant="tertiary" onClick={handleClose}>
            Cancel
          </Button>

          <WhatsAppLoginButton
            scope="whatsapp_business_management,whatsapp_business_messaging"
            onSuccess={(whatsAppSignUpData) => {
              handleWhatsAppAuthenticated(whatsAppSignUpData);
              handleAddPhoneNumber({
                wabaId: whatsAppSignUpData.waba_id,
              });

              handleClose();
            }}
            platform="whatsapp"
          >
            Continue
          </WhatsAppLoginButton>
        </FlexBox>
      </Modal>

      <WhatsAppConnectModal />
    </>
  );
};
